import React from "react";
import { Navigate, RouteProps } from "react-router-dom";
import LegacyRedirect from "./LegacyRedirect";
// Lazy like every other page: PayRequest pulls in @adyen/adyen-web (+ CSS),
// which as a static import landed in the entry chunk for every supplier.
const PayRequest = React.lazy(() => import("../pages/request-service/PayRequest"));
const SetSellingPrices = React.lazy(
  () => import("../pages/request-service/PayRequest/SetSellingPrices")
);

// auth
const Login = React.lazy(() => import("../pages/auth/Login"));
const ForgetPassword = React.lazy(() => import("../pages/auth/ForgetPassword"));
const ResetPasword = React.lazy(() => import("../pages/auth/ResetPassword"));
const Logout = React.lazy(() => import("../pages/auth/Logout"));

const RegisterWithMail = React.lazy(
  () => import("../pages/auth/RegisterWithMail")
);
const Maintenance = React.lazy(() => import("../pages/other/Maintenance"));
const Error404 = React.lazy(() => import("../pages/error/Error404"));
const Error500 = React.lazy(() => import("../pages/error/Error500"));

const Dashboard = React.lazy(() => import("../pages/dashboard"));
const DashboardEntry = React.lazy(() => import("../pages/referrer-dashboard/DashboardEntry"));

const Events = React.lazy(() => import("../pages/events"));
const EventsAddEdit = React.lazy(
  () => import("../pages/events/add-edit/index")
);
const Invoices = React.lazy(() => import("../pages/invoices"));
const InvoicesDetail = React.lazy(
  () => import("../pages/invoices/detail/index")
);
const InvoiceCreate = React.lazy(
  () => import("../pages/invoices/create/index")
);

const EventsAttendees = React.lazy(() => import("../pages/events/attendees"));

const Hotels = React.lazy(() => import("../pages/hotels"));
const HotelsAddEdit = React.lazy(
  () => import("../pages/hotels/add-edit/index")
);
const HotelDetail = React.lazy(() => import("../pages/hotels/Detail"));
const RestaurantDetail = React.lazy(() => import("../pages/restaurants/Detail"));
const EventDetail = React.lazy(() => import("../pages/events/Detail"));
const ProductDetail = React.lazy(() => import("../pages/products/Detail"));

const Tickets = React.lazy(() => import("../pages/tickets"));
const Contracts = React.lazy(() => import("../pages/contracts"));
const Documentation = React.lazy(() => import("../pages/documentation"));
const TicketAddEdit = React.lazy(
  () => import("../pages/tickets/add-edit/index")
);
// Docs-deflection wizard wraps the create form (edit keeps TicketAddEdit).
const TicketCreateWizard = React.lazy(
  () => import("../pages/tickets/create-wizard")
);

const Restaurants = React.lazy(() => import("../pages/restaurants"));
const RestaurantManage = React.lazy(
  () => import("../pages/restaurants/manage")
);
const RestaurantAddEdit = React.lazy(
  () => import("../pages/restaurants/add-edit/")
);

const Offers = React.lazy(() => import("../pages/offers"));
const OfferDetail = React.lazy(() => import("../pages/offers/detail"));
const UploadContract = React.lazy(() => import("../pages/offers/upload-contract"));

const RequestService = React.lazy(() => import("../pages/request-service"));
const CreateRequest = React.lazy(
  () => import("../pages/request-service/CreateRequest")
);
const RequestDetail = React.lazy(
  () => import("../pages/request-service/RequestDetail")
);
const CustomerRequestDetail = React.lazy(
  () => import("../pages/new-request-service/detail")
);
const AvailableRequests = React.lazy(
  () => import("../pages/available-requests")
);
const CreateOffer = React.lazy(
  () => import("../pages/request-service/create-offer")
);
const PaymentLink = React.lazy(
  () => import("../pages/request-service/PayRequest/PaymentLink")
);

const Chat = React.lazy(() => import("../pages/Chat"));
const SupplierRegister = React.lazy(() => import("../pages/supplier-register"));
const ReferrerRegister = React.lazy(() => import("../pages/referrer-register"));
const ReferralLinks = React.lazy(() => import("../pages/referral-links"));
const MyCustomers = React.lazy(() => import("../pages/my-customers"));
// Referral landing designs (2026-10-19): public, outside the portal layout.
const JoinPage = React.lazy(() => import("../pages/join"));
const JoinPreviewPage = React.lazy(() => import("../pages/join").then((m) => ({ default: m.JoinPreviewPage })));
const Referees = React.lazy(() => import("../pages/referees"));
const ReferralEarningsPage = React.lazy(() => import("../pages/referral-earnings"));
const RefereeDetail = React.lazy(() => import("../pages/referees/detail"));
const Services = React.lazy(() => import("../pages/services"));
const Profile = React.lazy(() => import("../pages/profile"));
const NotificationChannels = React.lazy(() => import("../pages/notification-channels"));

// AI Request Dispatcher (plan §3.4/§6). Request preferences are always
// available (supplier input); My Scorecard is admin-flag-gated — the page
// itself probes /dispatch/my-scorecard and bounces to /error-404 when the
// backend answers 404 (flag off), so no gated content ever renders.
// Settings → Billing details: a supplier's own billing record (2026-11-03).
const SettingsBilling = React.lazy(() => import("../pages/settings-billing"));
const RequestPreferences = React.lazy(
  () => import("../pages/request-preferences")
);
const MyScorecard = React.lazy(() => import("../pages/my-scorecard"));

// Aircraft Module
const AircraftList = React.lazy(() => import("../pages/aircrafts/List"));
const AircraftAddEdit = React.lazy(() => import("../pages/aircrafts/AddEdit"));
const AircraftSeatMaps = React.lazy(() => import("../pages/aircrafts/SeatMaps"));
const AircraftMedia = React.lazy(() => import("../pages/aircrafts/Media"));
const AircraftDetailLayout = React.lazy(() => import("../pages/aircrafts/DetailLayout"));
const AircraftDetails = React.lazy(() => import("../pages/aircrafts/Details"));

// Empty Legs Module
const EmptyLegs = React.lazy(() => import("../pages/empty-legs"));
const EmptyLegAddEdit = React.lazy(() => import("../pages/empty-legs/AddEdit"));
const EmptyLegDetail = React.lazy(() => import("../pages/empty-legs/Detail"));
const EmptyLegPassengers = React.lazy(() => import("../pages/empty-legs/Passengers"));

// Yachts Module
const Yachts = React.lazy(() => import("../pages/yachts"));
const YachtAddEdit = React.lazy(() => import("../pages/yachts/AddEdit"));
const YachtDetailLayout = React.lazy(() => import("../pages/yachts/DetailLayout"));

// Experiences Module
const Experiences = React.lazy(() => import("../pages/experiences"));
const ExperienceAddEdit = React.lazy(() => import("../pages/experiences/AddEdit"));
const ExperienceDetailLayout = React.lazy(() => import("../pages/experiences/DetailLayout"));

// Cars Module
const Cars = React.lazy(() => import("../pages/cars"));
const CarAddEdit = React.lazy(() => import("../pages/cars/AddEdit"));
const CarDetailLayout = React.lazy(() => import("../pages/cars/DetailLayout"));

const ProductList = React.lazy(() => import("../pages/products/List"));
const ProductAddEdit = React.lazy(() => import("../pages/products/AddEdit"));

const SupplierTerms = React.lazy(() => import("../pages/terms"));
const PrivacyPolicy = React.lazy(() => import("../pages/privacy"));
const AffiliateTerms = React.lazy(() => import("../pages/affiliate-terms"));

// Theme-migration shell preview (public, no auth) — see layouts/PortalLayout
const PortalLayoutPreview = React.lazy(
  () => import("../pages/portal-layout-preview")
);

// KYB onboarding + compliance — first live PortalLayout adoption. Each page
// self-mounts the shell, so it is registered as a public (pass-through) route
// and self-guards auth internally.
const Onboarding = React.lazy(() => import("../pages/onboarding"));
const Compliance = React.lazy(() => import("../pages/compliance"));
const InventoryCompliance = React.lazy(() => import("../pages/inventory-compliance"));
const Bank = React.lazy(() => import("../pages/bank"));
const Security = React.lazy(() => import("../pages/security"));
const Team = React.lazy(() => import("../pages/team"));
const ApiKeys = React.lazy(() => import("../pages/api-keys"));
const Promotions = React.lazy(() => import("../pages/promotions"));
const ApplicationForm = React.lazy(() => import("../pages/application-form"));
const BecomeServiceProvider = React.lazy(() => import("../pages/become-service-provider"));
const Questionnaire = React.lazy(() => import("../pages/questionnaire"));
const IndividualApplication = React.lazy(() => import("../pages/individual-application"));
const ServiceRequirements = React.lazy(() => import("../pages/service-requirements"));
const ServiceDocuments = React.lazy(() => import("../pages/service-documents"));
const CompanyProfile = React.lazy(() => import("../pages/company-profile"));

// Finance — balances, withdrawals and the supplier's own statement.
const Finance = React.lazy(() => import("../pages/finance"));
const FinanceStatement = React.lazy(() => import("../pages/finance/statement"));


export interface RoutesProps {
  path: RouteProps["path"];
  name?: string;
  element?: RouteProps["element"];
  route?: any;
  exact?: boolean;
  icon?: string;
  header?: string;
  permissions?: string[];
  children?: RoutesProps[];
}

// root routes
const rootRoute: RoutesProps = {
  path: "/",
  exact: true,
  element: <Navigate to="/dashboard" />,
};

// dashboards

const dashboardRoutes: RoutesProps = {
  path: "/dashboard",
  name: "Dashboard",
  icon: "airplay",
  element: <DashboardEntry fallback={<Dashboard />} />,
};

const parameterRoutes: RoutesProps = {
  path: "/parameters",
  name: "Parameters",
  icon: "lock",
  element: "",
  children: [
    {
      path: "/supplier/hotels",
      name: "Hotels",
      permissions: ["supplier_see_hotels"],
      element: <Hotels />,
      children: [
        {
          path: "/supplier/hotels/new",
          name: "Add Hotel",
          permissions: ["supplier_create_hotel"],
          element: <HotelsAddEdit />,
        },
        {
          path: "/supplier/hotels/:id/edit",
          name: "Edit Hotel",
          permissions: ["supplier_manage_hotels"],
          element: <HotelsAddEdit />,
        },
        {
          path: "/supplier/hotels/:id/*",
          name: "Hotel",
          permissions: ["supplier_see_hotels"],
          element: <HotelDetail />,
        },
        // Old URL shapes.
        { path: "/supplier/hotels/add", element: <LegacyRedirect to={() => "/supplier/hotels/new"} /> },
        { path: "/supplier/hotels/edit/:id", element: <LegacyRedirect to={(p) => `/supplier/hotels/${p.id}/edit`} /> },
      ],
    },
    {
      // No permission gate — every supplier user can view their signed contracts.
      path: "/supplier/contracts",
      name: "Contracts",
      element: <Contracts />,
    },
    {
      path: "/supplier/tickets",
      name: "Tickets",
      permissions: ["supplier_get_ticket"],
      element: <Tickets />,
      children: [
        {
          path: "/supplier/tickets/add",
          name: "Add Ticket",
          permissions: ["supplier_create_ticket"],
          element: <TicketCreateWizard />,
        },
        {
          path: "/supplier/tickets/edit/:id",
          name: "Edit Ticket",
          permissions: ["supplier_view_detail_ticket"],
          element: <TicketAddEdit />,
        },
      ],
    },
    {
      path: "/supplier/documentation",
      name: "Supplier Guide",
      permissions: [],
      element: <Documentation />,
    },
    {
      path: "/supplier/events",
      name: "Events",
      permissions: ["supplier_view_events"],
      element: <Events />,
      children: [
        {
          path: "/supplier/events/new",
          name: "Add Events",
          permissions: ["supplier_create_events"],
          element: <EventsAddEdit />,
        },
        {
          path: "/supplier/events/:id/edit",
          name: "Edit Events",
          permissions: ["supplier_update_events"],
          element: <EventsAddEdit />,
        },
        {
          path: "/supplier/events/:id/attendees",
          name: "Event Attendees",
          permissions: ["supplier_update_events"],
          element: <EventsAttendees />,
        },
        {
          path: "/supplier/events/:id/*",
          name: "Event",
          permissions: ["supplier_view_events"],
          element: <EventDetail />,
        },
        // Old URL shapes.
        { path: "/supplier/events/add", element: <LegacyRedirect to={() => "/supplier/events/new"} /> },
        { path: "/supplier/events/edit/:id", element: <LegacyRedirect to={(p) => `/supplier/events/${p.id}/edit`} /> },
      ],
    },
    {
      path: "/supplier/invoices",
      name: "Invoices",
      permissions: ["supplier_view_supplier_invoices"],
      element: <Invoices />,
      children: [
        {
          path: "/supplier/invoices/detail/:id",
          name: "Invoice Detail",
          permissions: ["supplier_view_supplier_invoices"],
          element: <InvoicesDetail />,
        },
        {
          path: "/supplier/invoices/create",
          name: "Create Invoice",
          permissions: ["supplier_create_invoices"],
          element: <InvoiceCreate />,
        },
      ],
    },
    {
      path: "/supplier/restaurants",
      name: "Restaurants",
      permissions: ["supplier_see_restaurant"],
      element: <Restaurants />,
      children: [
        {
          path: "/supplier/restaurants/new",
          name: "Add Restaurant",
          permissions: ["add_supplier_restaurant"],
          element: <RestaurantAddEdit />,
        },
        {
          path: "/supplier/restaurants/:id/edit",
          name: "Edit Restaurant",
          permissions: ["update_supplier_restaurant"],
          element: <RestaurantAddEdit />,
        },
        {
          path: "/supplier/restaurants/manage/:id",
          name: "Manage Restaurant",
          permissions: ["see_supplier_restaurant_detail"],
          element: <RestaurantManage />,
        },
        {
          path: "/supplier/restaurants/:id/*",
          name: "Restaurant",
          permissions: ["supplier_see_restaurant"],
          element: <RestaurantDetail />,
        },
        // Old URL shapes.
        { path: "/supplier/restaurants/add", element: <LegacyRedirect to={() => "/supplier/restaurants/new"} /> },
        { path: "/supplier/restaurants/edit/:id", element: <LegacyRedirect to={(p) => `/supplier/restaurants/${p.id}/edit`} /> },
      ],
    },
    {
      path: "/supplier/aircrafts",
      name: "Aircrafts",
      permissions: ["supplier_aircraft_read"],
      element: <AircraftList />,
      icon: "uil-plane",
      children: [
        {
          path: "/supplier/aircrafts/new",
          name: "New Aircraft",
          permissions: ["supplier_aircraft_write"],
          element: <AircraftAddEdit />,
        },
        {
          path: "/supplier/aircrafts/:id/*",
          element: <AircraftDetailLayout />,
          permissions: ["supplier_aircraft_read"],
        },
        {
          path: "/supplier/aircrafts/:id",
          element: <AircraftDetailLayout />,
          permissions: ["supplier_aircraft_read"],
        },
        {
          path: "/supplier/aircrafts/:id/edit",
          name: "Edit Aircraft",
          permissions: ["supplier_aircraft_write"],
          element: <AircraftAddEdit />,
        }
      ]
    },
    {
      path: "/supplier/empty-legs",
      name: "Empty Legs",
      permissions: ["supplier_emptyleg_read"],
      element: <EmptyLegs />,
      children: [
        {
          path: "/supplier/empty-legs/new",
          name: "New Empty Leg",
          permissions: ["supplier_emptyleg_write"],
          element: <EmptyLegAddEdit />,
        },
        {
          path: "/supplier/empty-legs/:id",
          name: "Empty Leg Detail",
          permissions: ["supplier_emptyleg_read"],
          element: <EmptyLegDetail />,
        },
        {
          path: "/supplier/empty-legs/:id/edit",
          name: "Edit Empty Leg",
          permissions: ["supplier_emptyleg_write"],
          element: <EmptyLegAddEdit />,
        },
        {
          path: "/supplier/empty-legs/:id/passengers",
          name: "Empty Leg Passengers",
          permissions: ["supplier_emptyleg_read"],
          element: <EmptyLegPassengers />,
        }
      ]
    },
    {
      path: "/supplier/yachts",
      name: "Yachts",
      permissions: ["supplier_yachts_read"],
      element: <Yachts />,
      children: [
        {
          path: "/supplier/yachts/new",
          name: "New Yacht",
          permissions: ["supplier_yachts_write"],
          element: <YachtAddEdit />,
        },
        {
          // Static `edit` outranks the `:id/*` detail splat in React Router v6. The form has
          // always supported edit mode; nothing routed to it.
          path: "/supplier/yachts/:id/edit",
          name: "Edit Yacht",
          permissions: ["supplier_yachts_write"],
          element: <YachtAddEdit />,
        },
        {
          path: "/supplier/yachts/:id/*",
          name: "Yacht Detail",
          permissions: ["supplier_yachts_read"],
          element: <YachtDetailLayout />,
        },
      ],
    },
    {
      path: "/supplier/experiences",
      name: "Experiences",
      permissions: ["supplier_manage_experiences"],
      element: <Experiences />,
      children: [
        {
          path: "/supplier/experiences/new",
          name: "New Experience",
          permissions: ["supplier_manage_experiences"],
          element: <ExperienceAddEdit />,
        },
        {
          path: "/supplier/experiences/:id/edit",
          name: "Edit Experience",
          permissions: ["supplier_manage_experiences"],
          element: <ExperienceAddEdit />,
        },
        {
          path: "/supplier/experiences/:id/*",
          name: "Experience Detail",
          permissions: ["supplier_manage_experiences"],
          element: <ExperienceDetailLayout />,
        },
      ],
    },
    {
      path: "/supplier/cars",
      name: "Cars",
      permissions: ["supplier_cars_read"],
      element: <Cars />,
      children: [
        {
          path: "/supplier/cars/new",
          name: "New Car",
          permissions: ["supplier_cars_write"],
          element: <CarAddEdit />,
        },
        {
          path: "/supplier/cars/:id/edit",
          name: "Edit Car",
          permissions: ["supplier_cars_write"],
          element: <CarAddEdit />,
        },
        {
          path: "/supplier/cars/:id/*",
          name: "Car Detail",
          permissions: ["supplier_cars_read"],
          element: <CarDetailLayout />,
        },
        {
          path: "/supplier/cars/:id",
          name: "Car Detail",
          permissions: ["supplier_cars_read"],
          element: <CarDetailLayout />,
        },
      ],
    },
    {
      path: "/supplier/offers",
      name: "Offers",
      permissions: ["supplier_request_service_create_offer"],
      element: <Offers />,
      children: [
        /*{
                                                    path: "/supplier/offers/manage/:id",
                                                    name: "Manage Offers",
                                                    permissions: ["admin_manage_offers"],
                                                    element: <RestaurantManage/>,
                                                },*/
        {
          path: "/supplier/offers/:tab",
          name: "Offers",
          permissions: ["supplier_request_service_create_offer"],
          element: <Offers />,
        },
        {
          path: "/supplier/offers/detail/:requestServiceId",
          name: "Offers",
          permissions: ["supplier_request_service_create_offer"],
          element: <CreateOffer fromOffer={true} />,
        },
        {
          path: "/supplier/offers/:tab/detail/:id",
          name: "Offers",
          permissions: ["supplier_request_service_create_offer"],
          element: <OfferDetail />,
        },
        {
          // Attach the contract to an offer the customer has confirmed. Same
          // permission as updating an offer's status, which is what this does.
          path: "/supplier/offers/:id/upload-contract",
          name: "Upload Contract",
          permissions: ["supplier_update_offer_status"],
          element: <UploadContract />,
        },
      ],
    },
    {
      path: "/supplier/available-requests",
      name: "Available Requests",
      permissions: ["supplier_available_list_request_service"],
      element: <AvailableRequests />,
    },
    {
      path: "/supplier/requests",
      name: "Requests",
      permissions: ["supplier_list_request_services"],
      element: <RequestService />,
      children: [
        {
          path: "/supplier/requests/add-request",
          name: "Add Request",
          permissions: ["supplier_list_request_services"],
          element: <CreateRequest />,
        },
        {
          path: "/supplier/requests/:requestId/add-request",
          name: "Add Request",
          permissions: ["supplier_list_request_services"],
          element: <CreateRequest />,
        },
        {
          path: "/supplier/requests/:requestId/edit-request",
          name: "Edit Request",
          permissions: ["supplier_list_request_services"],
          element: <CreateRequest />,
        },
        {
          path: "/supplier/requests/:requestId/request-detail",
          name: "Edit Request",
          permissions: ["supplier_list_request_services"],
          element: <RequestDetail />,
        },
        {
          path: "/supplier/requests/:requestId/request-payment/:invoiceId",
          name: "Pay Request",
          permissions: ["supplier_list_request_services"],
          element: <PayRequest />,
        },
        {
          path: "/supplier/requests/:requestId/request-payment/set-selling-prices",
          name: "Set Selling Prices Request",
          permissions: ["supplier_list_request_services"],
          element: <SetSellingPrices />,
        },
        {
          path: "/supplier/requests/:requestId/payment-link",
          name: "Copy Payment Link",
          permissions: ["supplier_list_request_services"],
          element: <PaymentLink />,
        },
        {
          path: "/supplier/requests/:requestServiceId/make-offer",
          name: "Create Offer",
          permissions: ["supplier_list_request_services"],
          element: <CreateOffer />,
        },
        {
          path: "/supplier/requests/:requestServiceId/make-offer/:offerId",
          name: "Edit Offer",
          permissions: ["supplier_list_request_services"],
          element: <CreateOffer />,
        },
        {
          path: "/supplier/requests/:requestServiceId/offer-detail/:offerId",
          name: "See Offer Detail",
          permissions: ["supplier_list_request_services"],
          element: <CreateOffer />,
        },
        {
          path: "/supplier/requests/:tab",
          name: "Request Services",
          permissions: ["supplier_list_request_services"],
          element: <RequestService />,
        },
      ],
    },
    {
      path: "/supplier/new-request-services/:requestId",
      name: "Customer Request Detail",
      permissions: ["supplier_list_request_services"],
      element: <CustomerRequestDetail />,
    },
    {
      path: "/supplier/services",
      name: "Services",
      permissions: ["supplier_see_services"],
      element: <Services />,
    },
    {
      path: "/supplier/support/chat",
      name: "Chat Support",
      element: <Chat />,
    },
    {
      path: "/supplier/support/chat/:chatroomId",
      name: "Chat Support",
      element: <Chat />,
    },
    {
      path: "/profile",
      name: "Profile",
      element: <Profile />,
    },
    {
      path: "/notification-channels",
      name: "Notification Channels",
      element: <NotificationChannels />,
    },
    {
      path: "/request-preferences",
      name: "Request Preferences",
      element: <RequestPreferences />,
    },
    {
      path: "/settings/billing",
      name: "Billing details",
      // The permission GET/PUT /supplier-api/billing-details take.
      permissions: ["supplier_company_logo_update"],
      element: <SettingsBilling />,
    },
    {
      path: "/supplier/my-scorecard",
      name: "My Scorecard",
      element: <MyScorecard />,
    },
  ],
};

// auth
const authRoutes: RoutesProps[] = [
  {
    path: "/auth/login",
    name: "Login",
    element: <Login />,
  },
  {
    path: "/auth/logout",
    name: "Logout",
    element: <Logout />,
  },
  {
    path: "/auth/forget-password",
    name: "Forget Password",
    element: <ForgetPassword />,
  },
  {
    path: "/auth/reset-password",
    name: "Reset Password",
    element: <ResetPasword />,
  },
];

// public routes
const otherPublicRoutes = [
  {
    path: "/supplier-invite",
    name: "Register With Mail",
    element: <RegisterWithMail />,
  },
  {
    path: "/supplier-register",
    name: "Register With Form",
    element: <SupplierRegister />,
  },
  {
    // CON-69: same signup with Individual preselected (PRD link target).
    path: "/individual-register",
    name: "Individual Register",
    element: <SupplierRegister initialType="INDIVIDUAL" />,
  },
  {
    path: "/referrer-register",
    name: "Referrer Register",
    element: <ReferrerRegister />,
  },
  {
    // Referral landing designs (2026-10-19): /join/preview/<style> must precede /join/:code.
    path: "/join/preview/:style",
    name: "Referral Landing Preview",
    element: <JoinPreviewPage />,
  },
  {
    path: "/join/:code",
    name: "Referral Landing",
    element: <JoinPage />,
  },
  {
    path: "/maintenance",
    name: "Maintenance",
    element: <Maintenance />,
  },
  {
    path: "/error-404",
    name: "Error - 404",
    element: <Error404 />,
  },
  {
    path: "/error-500",
    name: "Error - 500",
    element: <Error500 />,
  },
  {
    path: "/terms",
    name: "Supplier Terms & Conditions",
    element: <SupplierTerms />,
  },
  {
    path: "/privacy",
    name: "Privacy Policy",
    element: <PrivacyPolicy />,
  },
  {
    path: "/affiliate-terms",
    name: "Affiliate Conditions",
    element: <AffiliateTerms />,
  },
  {
    path: "/portal-layout-preview",
    name: "PortalLayout Preview",
    element: <PortalLayoutPreview />,
  },
];
// flatten the list of all nested routes
const flattenRoutes = (routes: RoutesProps[]) => {
  let flatRoutes: RoutesProps[] = [];

  routes = routes || [];
  routes.forEach((item: RoutesProps) => {
    flatRoutes.push(item);

    if (typeof item.children !== "undefined") {
      flatRoutes = [...flatRoutes, ...flattenRoutes(item.children)];
    }
  });
  return flatRoutes;
};

const productRoutes: RoutesProps = {
  path: "/supplier/products",
  name: "Products",
  icon: "shopping-bag",
  element: <ProductList />,
  children: [
    {
      path: "/supplier/products/new",
      name: "Create Product",
      element: <ProductAddEdit />,
    },
    {
      path: "/supplier/products/:id/edit",
      name: "Edit Product",
      element: <ProductAddEdit />,
    },
    {
      path: "/supplier/products/:id/*",
      name: "Product",
      element: <ProductDetail />,
    },
    // Old URL shapes.
    { path: "/supplier/products/create", element: <LegacyRedirect to={() => "/supplier/products/new"} /> },
    { path: "/supplier/products/edit/:id", element: <LegacyRedirect to={(p) => `/supplier/products/${p.id}/edit`} /> },
  ],
};

// All routes
const authProtectedRoutes: any = [
  rootRoute,
  dashboardRoutes,
  productRoutes,
  parameterRoutes,
  // Onboarding (KYB) pages — rendered in the standard VerticalLayout shell like
  // every other authenticated supplier page (previously standalone public routes
  // that self-mounted PortalLayout, which gave them a different sidebar). The
  // PrivateRoute gate is auth-only, so a not-yet-live supplier can still reach
  // onboarding; the KYB steps live in the in-page OnboardingSubnav rail.
  { path: "/supplier/onboarding", name: "Onboarding", element: <Onboarding /> },
  { path: "/supplier/application-form", name: "Supplier Application Form", element: <ApplicationForm /> },
  // An affiliate's optional upgrade to a service provider (2026-10-19).
  { path: "/supplier/become-service-provider", name: "Become a service provider", element: <BecomeServiceProvider /> },
  { path: "/supplier/questionnaire", name: "Compliance Questionnaire", element: <Questionnaire /> },
  // CON-70..74: an Individual (broker) supplier's onboarding.
  { path: "/supplier/individual-application", name: "Individual Application", element: <IndividualApplication /> },
  { path: "/supplier/compliance", name: "My Compliance Documents", element: <Compliance /> },
  { path: "/supplier/inventory-compliance", name: "Inventory Compliance", element: <InventoryCompliance /> },
  // Onboarding step: the company's own identity + contact details.
  { path: "/supplier/company-profile", name: "Company Profile", element: <CompanyProfile /> },
  // Onboarding step: per-service compliance, inside the onboarding rail.
  { path: "/supplier/service-documents", name: "Service Documents", element: <ServiceDocuments /> },
  // Legacy path, still a redirect for old links/bookmarks.
  { path: "/supplier/service-requirements", name: "Service Requirements", element: <ServiceRequirements /> },
  { path: "/supplier/bank", name: "Settlement Bank", element: <Bank /> },
  { path: "/supplier/finance", name: "Finance", element: <Finance />, permissions: ["supplier_see_finance"] },
  { path: "/supplier/referral-links", name: "Referral Links", element: <ReferralLinks />, permissions: ["supplier_manage_referral_links"] },
  { path: "/supplier/referees", name: "Referees", element: <Referees />, permissions: ["supplier_see_referrals"] },
  // Exclusive referrals: the customers this supplier referred ("Referred customers",
  // owner 2026-11-02; was "My customers"). The old path stays a redirect for old links.
  { path: "/supplier/referred-customers", name: "Referred customers", element: <MyCustomers />, permissions: ["supplier_see_exclusive_customers"] },
  { path: "/supplier/my-customers", name: "Referred customers", element: <Navigate to="/supplier/referred-customers" replace />, permissions: ["supplier_see_exclusive_customers"] },
  // Referral earnings also render as a tab on /supplier/finance; this is the
  // same block on a page of its own, for the Referrals section of the sidebar.
  { path: "/supplier/referral-earnings", name: "Referral Earnings", element: <ReferralEarningsPage />, permissions: ["supplier_see_finance"] },
  { path: "/supplier/referees/:supplierId", name: "Referee", element: <RefereeDetail />, permissions: ["supplier_see_referrals"] },
  { path: "/supplier/finance/statement", name: "Statement", element: <FinanceStatement />, permissions: ["supplier_see_finance"] },
  { path: "/supplier/security", name: "Security", element: <Security /> },
  { path: "/supplier/team", name: "Team", element: <Team />, permissions: ["supplier_see_users"] },
  // Self-service Listings-API keys (2026-10-21).
  { path: "/supplier/api-keys", name: "API keys", element: <ApiKeys />, permissions: ["supplier_manage_api_keys"] },
  // Supplier promotions (2026-10-27): stars, recommended cards.
  { path: "/supplier/promotions", name: "Promotions", element: <Promotions />, permissions: ["supplier_manage_promotions"] },
  /*
            extrapagesRoutes,
            uiRoutes,*/
];
const publicRoutes = [...authRoutes, ...otherPublicRoutes];

const authProtectedFlattenRoutes = flattenRoutes([...authProtectedRoutes]);
const publicProtectedFlattenRoutes = flattenRoutes([...publicRoutes]);
export {
  publicRoutes,
  authProtectedRoutes,
  authProtectedFlattenRoutes,
  publicProtectedFlattenRoutes,
};
